import React from "react";
import { View, Text, Image, Pressable } from "react-native";
import { Lock, User } from "iconsax-react-nativejs";
import { useTheme } from "@/src/styles/theme-provider";
import { useTranslation } from "react-i18next";
import defaultCover from "../../assets/images/default-group-cover.png";
import { useGetGroupMemberCount } from "@/src/services/api/groups/groups.hooks";

export type GroupCardProps = Readonly<{
  ItemId: string;
  name: string;
  description?: string;
  coverImageUrl: string;
  isPrivate: boolean;
  memberAvatars?: string[];
  onViewPress?: () => void;
  singleColumn?: boolean;
}>;

const GroupCard = ({
  ItemId,
  name,
  description,
  coverImageUrl,
  isPrivate,
  memberAvatars = [],
  onViewPress,
  singleColumn = false,
}: GroupCardProps) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const { data: memberCount } = useGetGroupMemberCount(ItemId);
  const mutedColor = themeVars["--color-slate-default"] as string;
  return (
    <View
      className={`${
        singleColumn ? "" : "flex-1"
      } bg-chalk rounded-2xl overflow-hidden md:border md:border-ash-3 p-2 gap-2`}
    >
      {/* Cover Image */}
      <Image
        source={coverImageUrl ? { uri: coverImageUrl } : defaultCover}
        className="bg-ash-3 rounded-xl"
        style={{
          width: "100%",
          height: 120,
        }}
        resizeMode="cover"
      />

      {/* Card Body */}
      <View
        className={`${
          singleColumn ? "" : "flex-1"
        } flex-col gap-2 px-1 pb-1`}
      >
        <View className="gap-0.5">
          {/* Group Name */}
          <Text className="text-sm font-bold text-onyx" numberOfLines={1}>
            {name}
          </Text>
          <Text className="text-xs text-slate" numberOfLines={1}>
            {description}
          </Text>

          {/* Meta row */}
          <View className="flex-row items-center flex-wrap gap-2 mt-1">
            {/* Privacy badge */}
            <View className="flex-row items-center gap-1 bg-ash px-2 py-0.5 rounded-full">
              {isPrivate ? (
                <Lock size={12} color={mutedColor} variant="Linear" />
              ) : (
                <User size={12} color={mutedColor} variant="Linear" />
              )}
              <Text className="text-xs text-slate">
                {isPrivate ? t("LABEL_PRIVATE_GROUP") : t("LABEL_PUBLIC_GROUP")}
              </Text>
            </View>

            <Text className="text-xs text-slate">
              {`${memberCount ?? 0} ${t("LABEL_TAB_MEMBERS")}`}
            </Text>
          </View>
        </View>

        {/* View button */}
        <Pressable
          onPress={onViewPress}
          style={{
            backgroundColor: themeVars["--color-primary-default"] as string,
          }}
          className="flex-row px-3 justify-center items-center py-2 rounded-xl mt-auto active:opacity-80 hover:opacity-90"
        >
          <Text className="text-white text-sm font-medium">
            {t("LABEL_VIEW_GROUP")}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

export default GroupCard;
